import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePortfolio } from "@/context/PortfolioContext";
import { fmtDate } from "@/lib/format";
import { AlertOctagon, AlertTriangle, Info, Wand2, Pencil, Search, EyeOff, RotateCcw, Loader2, Trash2 } from "lucide-react";

const SEV = {
    error: { icon: AlertOctagon, cls: "border-l-loss", tone: "text-loss", label: "Needs fixing" },
    warning: { icon: AlertTriangle, cls: "border-l-accent", tone: "text-accent", label: "Review" },
    info: { icon: Info, cls: "border-l-muted-foreground/40", tone: "text-muted-foreground", label: "Info" },
};

function TxnLine({ t }) {
    return (
        <div className="text-xs text-muted-foreground num" data-testid="issue-txn-line">
            {t.transaction_type} {t.quantity} {t.ticker} @ {t.price} · {t.sub_portfolio} · {fmtDate(t.transaction_date)}
        </div>
    );
}

function MoveSub({ issue, run }) {
    const { subs } = usePortfolio();
    const held = Object.fromEntries((issue.holding_subs || []).map((h) => [h.sub, h.shares]));
    const [sub, setSub] = useState(issue.fix?.params?.sub || "");
    return (
        <div className="flex items-center gap-1.5">
            <Select value={sub} onValueChange={setSub}>
                <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`issue-sub-select-${issue.key}`}><SelectValue placeholder="Sub-portfolio" /></SelectTrigger>
                <SelectContent>
                    {subs.map((s) => (
                        <SelectItem key={s.id} value={s.name}>{s.name}{held[s.name] ? ` · holds ${held[s.name]} sh` : ""}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!sub || sub === issue.txn.sub_portfolio}
                onClick={() => run({ action: "move_sub", params: { txn_id: issue.txn.id, sub } })} data-testid={`issue-move-btn-${issue.key}`}>Move</Button>
        </div>
    );
}

function SetDate({ issue, run }) {
    const [d, setD] = useState(issue.fix?.params?.date || issue.txn.transaction_date);
    return (
        <div className="flex items-center gap-1.5">
            <Input type="date" value={d} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setD(e.target.value)}
                className="h-8 w-[160px] text-xs num" data-testid={`issue-date-input-${issue.key}`} />
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!d || d === issue.txn.transaction_date}
                onClick={() => run({ action: "set_date", params: { txn_id: issue.txn.id, date: d } })} data-testid={`issue-date-btn-${issue.key}`}>Save date</Button>
        </div>
    );
}

function Rename({ issue, run }) {
    const [nt, setNt] = useState(issue.fix?.params?.new || "");
    return (
        <div className="flex items-center gap-1.5">
            <Input value={nt} onChange={(e) => setNt(e.target.value.toUpperCase())} placeholder="New ticker"
                className="h-8 w-[130px] text-xs num uppercase" data-testid={`issue-rename-input-${issue.key}`} />
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!nt || nt === issue.ticker}
                onClick={() => run({ action: "rename", params: { old: issue.ticker, new: nt } })} data-testid={`issue-rename-btn-${issue.key}`}>Rename</Button>
        </div>
    );
}

function ManualControls({ issue, run }) {
    if (issue.kind === "sell_wrong_sub") return <MoveSub issue={issue} run={run} />;
    if (issue.kind === "sell_before_buy") return <SetDate issue={issue} run={run} />;
    if (issue.kind === "rename" || issue.kind === "no_data") return <Rename issue={issue} run={run} />;
    return null;
}

export function IssueCard({ issue, onFix, onDismiss, onEdit, onInspect, onDelete }) {
    const [busy, setBusy] = useState(false);
    const s = SEV[issue.severity] || SEV.info;
    const Icon = s.icon;
    const run = async (payload) => { setBusy(true); try { await onFix(payload); } finally { setBusy(false); } };
    return (
        <Card className={`p-4 border-l-4 ${s.cls} ${issue.dismissed ? "opacity-60" : ""}`} data-testid={`issue-card-${issue.key}`}>
            <div className="flex items-start gap-3">
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.tone}`} />
                <div className="flex-1 min-w-0 space-y-2">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold" data-testid="issue-title">{issue.title}</h3>
                            <span className={`text-[10px] uppercase tracking-wider ${s.tone}`}>{s.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{issue.detail}</p>
                        {issue.txn && <TxnLine t={issue.txn} />}
                    </div>
                    {!issue.dismissed && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {issue.fix && (
                                <Button size="sm" className="h-8 text-xs gap-1" disabled={busy} onClick={() => run(issue.fix)} data-testid={`issue-fix-btn-${issue.key}`}>
                                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}{issue.fix.label}
                                </Button>
                            )}
                            <ManualControls issue={issue} run={run} />
                            {issue.txn && <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => onEdit(issue.txn)} data-testid={`issue-edit-btn-${issue.key}`}><Pencil className="w-3 h-3" />Edit</Button>}
                            {issue.txn && <Button size="sm" variant="ghost" className="h-8 text-xs gap-1 text-loss hover:text-loss" onClick={() => onDelete(issue.txn)} data-testid={`issue-delete-btn-${issue.key}`}><Trash2 className="w-3 h-3" />Delete</Button>}
                            <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => onInspect(issue.ticker)} data-testid={`issue-inspect-btn-${issue.key}`}><Search className="w-3 h-3" />Check position</Button>
                        </div>
                    )}
                </div>
                <button onClick={() => onDismiss(issue)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"
                    data-testid={`issue-dismiss-btn-${issue.key}`}>
                    {issue.dismissed ? <><RotateCcw className="w-3 h-3" />Restore</> : <><EyeOff className="w-3 h-3" />Dismiss</>}
                </button>
            </div>
        </Card>
    );
}
